import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

export type Motyw = "light" | "dark";

/** Klucz w localStorage. Ta sama nazwa siedzi w skrypcie inline w client/index.html. */
export const KLUCZ_MOTYWU = "convolens-theme";

interface ThemeContextValue {
  motyw: Motyw;
  /** true, dopoki klient nie dokonal wlasnego wyboru - wtedy chodzimy za systemem (TM-1). */
  zaSystemem: boolean;
  przelacz: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function czytajZDokumentu(): Motyw {
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

function czytajZapis(): Motyw | null {
  try {
    const v = window.localStorage.getItem(KLUCZ_MOTYWU);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    // Tryb prywatny albo zablokowane ciasteczka - brak zapisu to nie awaria.
    return null;
  }
}

function zastosuj(motyw: Motyw): void {
  document.documentElement.setAttribute("data-theme", motyw);
  document.documentElement.style.colorScheme = motyw;
}

export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  // ZRODLEM PRAWDY DLA RENDERU JEST document.documentElement, nie ten stan.
  // Skrypt inline w index.html ustawia atrybut, zanim React w ogole istnieje - liczenie
  // stanu poczatkowego od nowa (z localStorage i matchMedia) groziloby rozjazdem na jedna
  // klatke, gdyby kiedykolwiek rozjechala sie logika obu miejsc. React trzyma tu lustro
  // wylacznie po to, zeby przelacznik wiedzial, ktora ikone pokazac.
  const [motyw, setMotyw] = useState<Motyw>(czytajZDokumentu);
  const [zaSystemem, setZaSystemem] = useState<boolean>(() => czytajZapis() === null);

  // TM-2: dopoki klient nie wybral sam, chodzimy za systemem - takze gdy zmieni go
  // w trakcie sesji. Po wlasnym wyborze przestajemy sluchac.
  useEffect(() => {
    if (!zaSystemem) return;
    // Ten sam warunek co w bootstrapie w index.html. matchMedia nie istnieje
    // w starszych osadzonych widokach webowych i w jsdom - brak nasluchu ma
    // zdegradowac aplikacje do motywu ustawionego przy starcie, a nie ja wywalic.
    if (typeof window.matchMedia !== "function") return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const naZmiane = (e: MediaQueryListEvent): void => {
      const nowy: Motyw = e.matches ? "dark" : "light";
      zastosuj(nowy);
      setMotyw(nowy);
    };

    mq.addEventListener("change", naZmiane);
    return () => mq.removeEventListener("change", naZmiane);
  }, [zaSystemem]);

  const przelacz = useCallback(() => {
    // Liczymy z dokumentu, nie ze stanu - dokument jest zrodlem prawdy.
    const nowy: Motyw = czytajZDokumentu() === "dark" ? "light" : "dark";
    zastosuj(nowy);
    setMotyw(nowy);
    setZaSystemem(false);
    try {
      window.localStorage.setItem(KLUCZ_MOTYWU, nowy);
    } catch {
      // Zapis sie nie udal - motyw dziala do konca sesji, po prostu sie nie zapamieta.
    }
  }, []);

  const value = useMemo(() => ({ motyw, zaSystemem, przelacz }), [motyw, zaSystemem, przelacz]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
